import { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Loader2, TrendingUp, TrendingDown, Award, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const TeamEventsBreakdownModal = ({ 
  isOpen, 
  onClose, 
  teamId, 
  teamName,
  token 
}) => {
  const [breakdown, setBreakdown] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const fetchBreakdown = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/teams/${teamId}/events/breakdown`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setBreakdown(data);
      } else {
        toast.error('Errore nel caricamento dei dettagli');
      }
    } catch (err) {
      console.error('Error fetching breakdown:', err);
      toast.error('Errore di connessione');
    } finally {
      setIsLoading(false);
    }
  }, [teamId, token]);

  useEffect(() => {
    if (isOpen && teamId) {
      fetchBreakdown();
    }
  }, [isOpen, teamId, fetchBreakdown]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Award className="h-6 w-6 text-blue-600" />
            Dettaglio Punti: {teamName}
          </DialogTitle>
          <DialogDescription>
            Breakdown completo di tutti i bonus e malus ricevuti
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center p-12">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        ) : breakdown ? (
          <div className="space-y-6">
            {/* Totale Generale */}
            <div className="bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-950 dark:to-cyan-950 p-4 rounded-lg border-2 border-blue-300 dark:border-blue-700">
              <div className="flex items-center justify-between">
                <span className="text-lg font-semibold text-gray-800 dark:text-gray-200">
                  Totale Punti Team
                </span>
                <span className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                  {Math.round(breakdown.total_points)}
                </span>
              </div>
            </div>

            {/* Lista Professori */}
            {breakdown.professors.length === 0 ? (
              <div className="text-center p-8 text-gray-500 dark:text-gray-400">
                <AlertCircle className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>Nessun professore assegnato a questo team</p>
              </div>
            ) : (
              breakdown.professors.map((prof) => (
                <div
                  key={prof.professor_id}
                  className="border-2 border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all"
                >
                  {/* Header Professore */}
                  <div className="bg-gradient-to-r from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-900 p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                          {prof.professor_name}
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {prof.professor_subject}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                          {Math.round(prof.total_points)}
                        </p>
                        <p className="text-xs text-gray-600 dark:text-gray-400">punti totali</p>
                      </div>
                    </div>
                    
                    {/* Summary Badges */}
                    <div className="flex gap-3 mt-3">
                      {prof.total_bonus > 0 && (
                        <Badge className="bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-300 border-green-300 dark:border-green-700">
                          <TrendingUp className="h-3 w-3 mr-1" />
                          Bonus: +{Math.round(prof.total_bonus)}
                        </Badge>
                      )}
                      {prof.total_malus < 0 && (
                        <Badge className="bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300 border-red-300 dark:border-red-700">
                          <TrendingDown className="h-3 w-3 mr-1" />
                          Malus: {Math.round(prof.total_malus)}
                        </Badge>
                      )}
                    </div>
                  </div>

                  <div className="p-4 space-y-4">
                    {/* Sezione BONUS */}
                    {prof.bonus_events.length > 0 && (
                      <div>
                        <h4 className="flex items-center gap-2 text-sm font-bold text-green-700 dark:text-green-400 mb-2 uppercase tracking-wide">
                          <TrendingUp className="h-4 w-4" />
                          Bonus ({prof.bonus_events.length})
                        </h4>
                        <div className="space-y-2">
                          {prof.bonus_events.map((event) => (
                            <div
                              key={event.id}
                              className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-950/30 rounded-lg border border-green-200 dark:border-green-800 hover:bg-green-100 dark:hover:bg-green-950/50 transition-colors"
                            >
                              <div className="flex-1">
                                <p className="font-semibold text-gray-900 dark:text-gray-100 text-sm">
                                  {event.description}
                                </p>
                                <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                                  {new Date(event.created_at).toLocaleString('it-IT', {
                                    day: '2-digit',
                                    month: '2-digit',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </p>
                              </div>
                              <span className="text-lg font-bold text-green-700 dark:text-green-400 ml-3">
                                +{Math.round(event.points)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Sezione MALUS */}
                    {prof.malus_events.length > 0 && (
                      <div>
                        <h4 className="flex items-center gap-2 text-sm font-bold text-red-700 dark:text-red-400 mb-2 uppercase tracking-wide">
                          <TrendingDown className="h-4 w-4" />
                          Malus ({prof.malus_events.length})
                        </h4>
                        <div className="space-y-2">
                          {prof.malus_events.map((event) => (
                            <div
                              key={event.id}
                              className="flex items-center justify-between p-3 bg-red-50 dark:bg-red-950/30 rounded-lg border border-red-200 dark:border-red-800 hover:bg-red-100 dark:hover:bg-red-950/50 transition-colors"
                            >
                              <div className="flex-1">
                                <p className="font-semibold text-gray-900 dark:text-gray-100 text-sm">
                                  {event.description}
                                </p>
                                <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                                  {new Date(event.created_at).toLocaleString('it-IT', {
                                    day: '2-digit',
                                    month: '2-digit',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </p>
                              </div>
                              <span className="text-lg font-bold text-red-700 dark:text-red-400 ml-3">
                                {Math.round(event.points)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Nessun evento */}
                    {prof.bonus_events.length === 0 && prof.malus_events.length === 0 && (
                      <div className="text-center p-6 text-gray-500 dark:text-gray-400 text-sm">
                        Nessun evento registrato per questo professore
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        ) : (
          <div className="text-center p-8 text-gray-500 dark:text-gray-400">
            <AlertCircle className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>Impossibile caricare i dettagli</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
